import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import {
  BookOpen, ChevronRight, Plus, Loader, MessageCircle,
  Upload, FileText, Trash2, ChevronLeft, Clock, Brain,
  CheckCircle, AlertCircle, History, Layers, Send, BookMarked, X,
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { translations } from '../../translations';
import {
  getMyCourses, enrollWithCode, getMyNotifications,
  getStudentProfile, updateStudentProfile,
  uploadStudySet, extractStudyConcepts, listStudySets, getStudySet, deleteStudySet,
  getStudentHistory, fetchStudySetPdfBlob, uploadSelectionImage,
} from '../../services/studioApi';
import { authService } from '../../services/authService';
import { pixelChat, getPixelSession } from '../../services/api';
import UnifiedHeader from '../../components/UnifiedHeader';
import RightSideMenu from '../../components/RightSideMenu';
import SubscriptionModal from '../../components/SubscriptionModal';
import './Studio.css';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

const LEVEL_COLORS = { beginner: '#22c55e', intermediate: '#f59e0b', advanced: '#6366f1' };

function EnrollModal({ onEnroll, onClose, tStudent }) {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    if (!code.trim()) return;
    setLoading(true);
    setError('');
    try {
      const res = await enrollWithCode(code.trim());
      onEnroll(res.course_id);
    } catch (e) {
      setError(e.message || tStudent.invalidCode || 'Invalid invite code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="studio-modal-overlay" onClick={onClose}>
      <div className="studio-modal" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
        <h2 style={{ margin: '0 0 1rem', fontSize: '1.1rem' }}>
          {tStudent.joinCourse || 'Join a Course'}
        </h2>
        <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '1.25rem' }}>
          {tStudent.enterInviteCode || 'Enter the 6-character invite code from your teacher'}
        </p>
        <input
          className="form-input"
          value={code}
          onChange={e => setCode(e.target.value.toUpperCase())}
          placeholder="ABC123"
          maxLength={6}
          style={{ textAlign: 'center', fontSize: '1.5rem', fontWeight: 700, letterSpacing: 8 }}
          onKeyDown={e => e.key === 'Enter' && submit()}
        />
        {error && <p style={{ color: '#e53935', fontSize: '0.85rem', marginTop: '0.5rem' }}>{error}</p>}
        <div className="studio-modal-footer">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={submit} disabled={loading || code.length < 6}>
            {loading && <Loader size={15} style={{ animation: 'spin 1s linear infinite' }} />}
            {tStudent.joinBtn || 'Join Course'}
          </button>
        </div>
      </div>
    </div>
  );
}

function NewStudySetModal({ onCreated, onClose, tStudent }) {
  const [title, setTitle] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef();

  const submit = async () => {
    if (!file || !title.trim()) return;
    if (file.size > 25 * 1024 * 1024) {
      setError(tStudent.pdfTooLarge || 'PDF is too large. Maximum size is 25 MB.');
      return;
    }
    setUploading(true);
    setError('');
    try {
      const set = await uploadStudySet(file, title.trim());
      extractStudyConcepts(set.id).catch(() => {});
      onCreated(set, set.truncated);
    } catch (e) {
      setError(e.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="studio-modal-overlay" onClick={onClose}>
      <div className="studio-modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
        <h2 style={{ margin: '0 0 1rem', fontSize: '1.1rem' }}>
          {tStudent.newStudySet || 'New Study Set'}
        </h2>
        <label style={{ fontSize: '0.85rem', color: '#555', fontWeight: 600, display: 'block', marginBottom: '0.3rem' }}>
          {tStudent.studySetTitle || 'Study set name'}
        </label>
        <input
          className="form-input"
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder={tStudent.studySetTitle || 'e.g. Chapter 3 – Thermodynamics'}
          style={{ marginBottom: '1rem' }}
        />
        <div
          onClick={() => fileRef.current?.click()}
          style={{
            border: '2px dashed #c5d0e8', borderRadius: 10, padding: '1.5rem',
            textAlign: 'center', cursor: 'pointer', background: file ? '#f0fdf4' : '#f8f9ff',
            marginBottom: '1rem',
          }}
        >
          <input ref={fileRef} type="file" accept=".pdf" style={{ display: 'none' }} onChange={e => setFile(e.target.files[0])} />
          {file ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <FileText size={20} color="#22c55e" />
              <span style={{ color: '#15803d', fontWeight: 600, fontSize: '0.9rem' }}>{file.name}</span>
            </div>
          ) : (
            <>
              <Upload size={28} color="#5B35A0" style={{ marginBottom: 8 }} />
              <p style={{ margin: 0, color: '#5B35A0', fontWeight: 600, fontSize: '0.9rem' }}>
                {tStudent.uploadPdf || 'Upload PDF'}
              </p>
              <p style={{ margin: '0.25rem 0 0', color: '#888', fontSize: '0.8rem' }}>
                {tStudent.uploadPdfDesc || 'AI will extract learning concepts'}
              </p>
            </>
          )}
        </div>
        {error && <p style={{ color: '#e53935', fontSize: '0.85rem', marginBottom: '0.75rem' }}>{error}</p>}
        <div className="studio-modal-footer">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={submit} disabled={uploading || !file || !title.trim()}>
            {uploading && <Loader size={15} style={{ animation: 'spin 1s linear infinite' }} />}
            {uploading ? (tStudent.uploadingPdf || 'Uploading...') : (tStudent.uploadPdf || 'Upload PDF')}
          </button>
        </div>
      </div>
    </div>
  );
}

function KnowledgeLevelBadge({ level, tStudent, onChange }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const levels = ['beginner', 'intermediate', 'advanced'];
  const color = LEVEL_COLORS[level] || '#888';

  const select = async (l) => {
    setOpen(false);
    setSaving(true);
    try { await onChange(l); } finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: 'rgba(255,255,255,0.15)', border: '1.5px solid rgba(255,255,255,0.4)',
          borderRadius: 20, padding: '4px 12px', cursor: 'pointer', color: 'white', fontSize: '0.8rem',
        }}
      >
        <Brain size={14} />
        {saving ? <Loader size={12} style={{ animation: 'spin 1s linear infinite' }} /> : (
          <span style={{ fontWeight: 600, color: color === '#888' ? 'white' : color }}>
            {tStudent[level] || level}
          </span>
        )}
        <span style={{ opacity: 0.7, fontSize: '0.7rem' }}>▾</span>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '110%', right: 0, background: 'white',
          borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', zIndex: 100,
          minWidth: 160, overflow: 'hidden',
        }}>
          {levels.map(l => (
            <button
              key={l}
              onClick={() => select(l)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                padding: '0.65rem 1rem', background: l === level ? '#f5f3ff' : 'transparent',
                border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: '0.87rem',
                color: '#1a1a2e', fontWeight: l === level ? 700 : 400,
              }}
            >
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: LEVEL_COLORS[l], flexShrink: 0 }} />
              {tStudent[l] || l}
              {l === level && <CheckCircle size={13} color="#5B35A0" style={{ marginLeft: 'auto' }} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ===========================
// COURSES TAB
// ===========================
function CoursesTab({ courses, courseNotifs, loading, tStudent, onEnroll, navigate }) {
  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  if (courses.length === 0) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon"><BookOpen size={48} /></div>
        <h3>{tStudent.noCoursesTitle || 'No courses yet'}</h3>
        <p>{tStudent.noCoursesDesc || 'Join a course using an invite code from your teacher'}</p>
        <button className="btn-primary" onClick={onEnroll}>
          <Plus size={16} /> {tStudent.joinCourse || 'Join a Course'}
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="section-header">
        <h2><BookOpen size={18} />{tStudent.myCourses || 'My Courses'}</h2>
      </div>
      <div className="studio-card-grid">
        {courses.map(c => {
          const pct = Math.round(parseFloat(c.avg_mastery || 0) * 100);
          const unread = courseNotifs[c.id]?.unread || 0;
          return (
            <div key={c.id} className="studio-card" style={{ cursor: 'pointer', position: 'relative' }}
              onClick={() => navigate(`/studio/student/courses/${c.id}`)}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#1a1a2e', flex: 1 }}>{c.title}</h3>
                {(courseNotifs[c.id]?.total > 0) && (
                  <div style={{ position: 'relative', flexShrink: 0, padding: '2px' }}>
                    <MessageCircle size={18} color={unread > 0 ? '#667eea' : '#bbb'} />
                    {unread > 0 && (
                      <span style={{
                        position: 'absolute', top: -4, right: -4,
                        background: '#ef4444', color: '#fff', borderRadius: '50%',
                        minWidth: 16, height: 16, padding: '0 3px',
                        fontSize: '0.62rem', fontWeight: 700,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>{unread > 9 ? '9+' : unread}</span>
                    )}
                  </div>
                )}
              </div>
              {c.description && (
                <p style={{ margin: '0 0 0.75rem', color: '#666', fontSize: '0.82rem', lineHeight: 1.4 }}>
                  {c.description.slice(0, 80)}{c.description.length > 80 ? '…' : ''}
                </p>
              )}
              <div style={{ marginBottom: '0.75rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#888', marginBottom: '0.25rem' }}>
                  <span>{tStudent.overallProgress || 'Progress'}</span>
                  <span>{pct}%</span>
                </div>
                <div className="progress-bar-track">
                  <div className="progress-bar-fill" style={{ width: `${pct}%` }} />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.8rem', color: '#888' }}>
                <span>{c.concept_count || 0} {tStudent.concepts || 'concepts'}</span>
                <button className="btn-secondary" style={{ padding: '0.3rem 0.7rem', fontSize: '0.8rem' }}>
                  {tStudent.open || 'Open'} <ChevronRight size={13} />
                </button>
              </div>
            </div>
          );
        })}
        <div className="studio-card" style={{
          cursor: 'pointer', border: '2px dashed #c5d0e8',
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', minHeight: 150, gap: '0.75rem', background: '#f8f9ff',
        }} onClick={onEnroll}>
          <Plus size={28} color="#5B35A0" />
          <span style={{ color: '#5B35A0', fontWeight: 600, fontSize: '0.9rem' }}>
            {tStudent.joinAnotherCourse || 'Join Another Course'}
          </span>
        </div>
      </div>
    </>
  );
}

// ===========================
// TRANSCRIPT MODAL
// ===========================
function TranscriptModal({ sessionId, title, passageImageUrl, onClose, onContinueChat, tStudent }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getPixelSession(sessionId)
      .then(data => setMessages(data.messages || []))
      .catch(() => setMessages([]))
      .finally(() => setLoading(false));
  }, [sessionId]);

  return (
    <div className="studio-modal-overlay" onClick={onClose}>
      <div className="studio-modal" style={{ maxWidth: 560, maxHeight: '80vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>{title}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888' }}><X size={18} /></button>
        </div>
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {passageImageUrl && (
            <div style={{ marginBottom: '1rem', padding: '0.65rem', borderRadius: 10, background: '#faf8ff', border: '1px solid #e0d6f5' }}>
              <div style={{ fontSize: '0.72rem', color: '#5B35A0', fontWeight: 700, marginBottom: '0.4rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {tStudent.capturedRegion || 'Reference image'}
              </div>
              <img src={passageImageUrl} alt="reference" style={{ maxWidth: '100%', borderRadius: 6, display: 'block' }} />
            </div>
          )}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}><Loader size={24} style={{ animation: 'spin 1s linear infinite' }} /></div>
          ) : messages.length === 0 ? (
            <p style={{ color: '#888', textAlign: 'center' }}>No messages</p>
          ) : messages.map((m, i) => (
            <div key={i} style={{
              marginBottom: '0.75rem', padding: '0.65rem 0.9rem', borderRadius: 10,
              background: m.role === 'assistant' ? '#f5f3ff' : '#f0f4ff',
              fontSize: '0.87rem', lineHeight: 1.6,
            }}>
              <div style={{ fontWeight: 700, fontSize: '0.73rem', color: '#888', marginBottom: '0.3rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {m.role === 'assistant' ? 'Pixel AI' : 'You'}
              </div>
              {m.image_url && <img src={m.image_url} alt="" style={{ maxWidth: '100%', borderRadius: 6, marginBottom: '0.3rem', display: 'block' }} />}
              {m.role === 'assistant' ? (
                <ReactMarkdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>{m.content}</ReactMarkdown>
              ) : m.content}
            </div>
          ))}
        </div>
        <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem' }}>
          <button className="btn-secondary" onClick={onClose}>{tStudent.closeTranscript || 'Close'}</button>
          <button className="btn-primary" onClick={onContinueChat} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <MessageCircle size={14} /> {tStudent.continueChat || 'Continue Chat'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ===========================
// FULL-SCREEN CHAT OVERLAY
// ===========================
function FullScreenChat({ sessionId, initialMessages = [], studySetId, language, tStudent, onClose }) {
  const navigate = useNavigate();
  const [messages, setMessages] = useState(initialMessages);
  const [activeSessionId, setActiveSessionId] = useState(sessionId);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(initialMessages.length === 0 && Boolean(sessionId));
  const chatEndRef = useRef(null);

  // Lock page scroll while overlay is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    if (!sessionId || initialMessages.length > 0) return;
    getPixelSession(sessionId)
      .then(data => setMessages((data.messages || []).map(m => ({ role: m.role, content: m.content, imageUrl: m.image_url }))))
      .catch(() => {})
      .finally(() => setLoadingHistory(false));
  }, [sessionId]); // eslint-disable-line

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, loading]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setLoading(true);
    try {
      const res = await pixelChat({ sessionId: activeSessionId, message: text, subject: 'general', language: language || 'en', studySetId });
      setActiveSessionId(res.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: res.reply }]);
    } catch (e) {
      setMessages(prev => [...prev, { role: 'assistant', content: `Error: ${e.message}` }]);
    } finally { setLoading(false); }
  };

  const openInTutor = () => {
    navigate('/pixel-ai', { state: { sessionId: activeSessionId } });
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 10000, background: '#fff', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #e8edf5', display: 'flex', alignItems: 'center', gap: '0.75rem', background: 'white', flexShrink: 0 }}>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', color: '#5B35A0', padding: 4 }}>
          <ChevronLeft size={22} />
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 9, height: 9, borderRadius: '50%', background: 'linear-gradient(135deg,#5B35A0,#667eea)' }} />
          <span style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1a1a2e' }}>Pixel AI</span>
        </div>
      </div>

      {/* AnimLearn Tutor banner */}
      <div style={{ background: 'linear-gradient(90deg,#f0ebff,#e8f0ff)', borderBottom: '1px solid #e0d6f5', padding: '0.5rem 1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', flexShrink: 0 }}>
        <span style={{ fontSize: '0.78rem', color: '#5B35A0', lineHeight: 1.4 }}>
          💡 {tStudent.tutorBannerText || 'Get richer explanations, video generation and full history in AnimLearn Tutor.'}
        </span>
        <button
          onClick={openInTutor}
          style={{ background: '#5B35A0', color: 'white', border: 'none', borderRadius: 20, padding: '4px 14px', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
        >
          {tStudent.openInTutor || 'Open in Tutor'}
        </button>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '1rem 1rem 0.5rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {loadingHistory && (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#888' }}>
            <Loader size={22} style={{ animation: 'spin 1s linear infinite' }} />
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
            <div style={{
              maxWidth: '82%', padding: '0.65rem 1rem',
              borderRadius: m.role === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
              background: m.role === 'user' ? '#5B35A0' : '#f0ebff',
              color: m.role === 'user' ? 'white' : '#1a1a2e',
              fontSize: '0.9rem', lineHeight: 1.65,
            }}>
              {m.imageUrl && <img src={m.imageUrl} alt="" style={{ maxWidth: '100%', borderRadius: 8, marginBottom: '0.4rem', display: 'block' }} />}
              {m.role === 'assistant'
                ? <ReactMarkdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>{m.content}</ReactMarkdown>
                : m.content && <span>{m.content}</span>}
            </div>
          </div>
        ))}
        {loading && (
          <div style={{ display: 'flex', gap: 5, padding: '0.4rem 0' }}>
            {[0,1,2].map(i => <span key={i} style={{ width: 8, height: 8, borderRadius: '50%', background: '#5B35A0', opacity: 0.4, animation: `bounce 1s ${i*0.2}s infinite` }} />)}
          </div>
        )}
        <div ref={chatEndRef} />
      </div>

      {/* Input */}
      <div style={{ padding: '0.75rem 1rem', borderTop: '1px solid #e8edf5', display: 'flex', gap: '0.5rem', background: 'white', flexShrink: 0 }}>
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !e.shiftKey && send()}
          placeholder={tStudent.askFollowUp || 'Ask a follow-up…'}
          style={{ flex: 1, borderRadius: 24, border: '1.5px solid #e0d6f5', padding: '0.65rem 1.1rem', fontSize: '0.9rem', outline: 'none' }}
          autoFocus
        />
        <button onClick={send} disabled={!input.trim() || loading}
          style={{ background: '#5B35A0', border: 'none', borderRadius: '50%', width: 44, height: 44, cursor: 'pointer', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, opacity: (!input.trim() || loading) ? 0.5 : 1 }}>
          {loading ? <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={16} />}
        </button>
      </div>
    </div>
  );
}

// ===========================
// SELECTION POPUP
// ===========================
function SelectionPopup({ type, dataUrl, text, uploading, onAsk, onClose, tStudent }) {
  return (
    <div className="studio-modal-overlay" onClick={onClose}>
      <div className="studio-modal" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ margin: 0, fontSize: '1rem', color: '#5B35A0', fontWeight: 700 }}>
            {tStudent.askAboutSelection || 'Ask Pixel AI about this'}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888' }}>
            <X size={18} />
          </button>
        </div>

        {type === 'region' && dataUrl && (
          <img src={dataUrl} alt="captured region" style={{ width: '100%', borderRadius: 8, border: '1px solid #e8edf5', marginBottom: '1rem', display: 'block' }} />
        )}
        {type === 'text' && text && (
          <div style={{ background: '#f5f3ff', borderRadius: 8, padding: '0.85rem 1rem', marginBottom: '1rem', fontSize: '0.85rem', lineHeight: 1.7, color: '#333', fontStyle: 'italic', borderLeft: '3px solid #5B35A0', maxHeight: 220, overflowY: 'auto' }}>
            "{text}"
          </div>
        )}

        <button onClick={onAsk} disabled={uploading}
          style={{ width: '100%', background: 'linear-gradient(135deg,#5B35A0,#667eea)', color: 'white', border: 'none', borderRadius: 10, padding: '0.85rem', fontSize: '0.9rem', fontWeight: 700, cursor: uploading ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, opacity: uploading ? 0.7 : 1 }}>
          {uploading ? <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <BookMarked size={16} />}
          {uploading ? (tStudent.processing || 'Sending…') : (tStudent.askAboutSelection || 'Ask Pixel AI about this')}
        </button>
      </div>
    </div>
  );
}

// ===========================
// PDF READER + PASSAGE CHAT
// ===========================
function PdfReader({ set, tStudent, language }) {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pdfWidth, setPdfWidth] = useState(500);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(false);

  // Fetch PDF via authenticated proxy → blob URL (avoids CORS in PDF.js worker)
  useEffect(() => {
    if (!set.pdf_url) return;
    setPdfLoading(true);
    setPdfError(false);
    let objectUrl = null;
    fetchStudySetPdfBlob(set.id)
      .then(blob => {
        objectUrl = URL.createObjectURL(blob);
        setPdfBlobUrl(objectUrl);
      })
      .catch(() => setPdfError(true))
      .finally(() => setPdfLoading(false));
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [set.id, set.pdf_url]);

  // selection mode: 'text' | 'region'
  const [selectionMode, setSelectionMode] = useState('text');

  // text-mode
  const [selectionText, setSelectionText] = useState('');
  const [floatBtn, setFloatBtn] = useState(null);

  // region-mode drag
  const [dragStart, setDragStart] = useState(null);
  const [dragRect, setDragRect] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  // popup and fullscreen chat
  const [selectionPopup, setSelectionPopup] = useState(null); // {type, dataUrl, blob, text}
  const [fullscreenChat, setFullscreenChat] = useState(null); // {sessionId, initialMessages}
  const [uploading, setUploading] = useState(false);

  const pdfContainerRef = useRef(null);
  const pageWrapRef = useRef(null);

  // Measure PDF pane width for responsive page rendering
  useEffect(() => {
    if (!pdfContainerRef.current) return;
    const ro = new ResizeObserver(entries => {
      for (const entry of entries) {
        const w = Math.floor(entry.contentRect.width) - 32;
        if (w > 100) setPdfWidth(w);
      }
    });
    ro.observe(pdfContainerRef.current);
    return () => ro.disconnect();
  }, []);

  // Text-mode: floating Ask button on selection
  useEffect(() => {
    if (selectionMode !== 'text') return;
    const handle = () => {
      const sel = window.getSelection();
      const text = sel?.toString().trim();
      if (!text || text.length < 10 || !pdfContainerRef.current?.contains(sel.anchorNode)) { setFloatBtn(null); return; }
      try {
        const rect = sel.getRangeAt(0).getBoundingClientRect();
        setFloatBtn({ x: rect.left + rect.width / 2, y: rect.bottom + 8 });
        setSelectionText(text);
      } catch { setFloatBtn(null); }
    };
    document.addEventListener('selectionchange', handle);
    return () => document.removeEventListener('selectionchange', handle);
  }, [selectionMode]);

  // Region-mode: crop PDF canvas from drag rect
  const cropCanvas = useCallback((rect) => {
    if (!pageWrapRef.current) return;
    const canvas = pageWrapRef.current.querySelector('canvas');
    if (!canvas) return;
    const scaleX = canvas.width / canvas.offsetWidth;
    const scaleY = canvas.height / canvas.offsetHeight;
    const out = document.createElement('canvas');
    out.width = Math.max(1, Math.round(rect.w * scaleX));
    out.height = Math.max(1, Math.round(rect.h * scaleY));
    out.getContext('2d').drawImage(canvas, Math.round(rect.x * scaleX), Math.round(rect.y * scaleY), out.width, out.height, 0, 0, out.width, out.height);
    out.toBlob(blob => { if (blob) setSelectionPopup({ type: 'region', dataUrl: out.toDataURL('image/png'), blob }); }, 'image/png');
  }, []);

  const onPageMouseDown = useCallback((e) => {
    if (selectionMode !== 'region') return;
    e.preventDefault();
    const pr = pageWrapRef.current.getBoundingClientRect();
    setDragStart({ x: e.clientX - pr.left, y: e.clientY - pr.top });
    setDragRect(null); setIsDragging(true); setSelectionPopup(null);
  }, [selectionMode]);

  const onPageMouseMove = useCallback((e) => {
    if (!isDragging || !dragStart) return;
    const pr = pageWrapRef.current.getBoundingClientRect();
    const cx = e.clientX - pr.left, cy = e.clientY - pr.top;
    setDragRect({ x: Math.min(dragStart.x, cx), y: Math.min(dragStart.y, cy), w: Math.abs(cx - dragStart.x), h: Math.abs(cy - dragStart.y) });
  }, [isDragging, dragStart]);

  const onPageMouseUp = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);
    if (!dragRect || dragRect.w < 10 || dragRect.h < 10) { setDragRect(null); return; }
    cropCanvas(dragRect);
  }, [isDragging, dragRect, cropCanvas]);

  const onPageTouchStart = useCallback((e) => {
    if (selectionMode !== 'region') return;
    const t = e.touches[0];
    const pr = pageWrapRef.current.getBoundingClientRect();
    setDragStart({ x: t.clientX - pr.left, y: t.clientY - pr.top });
    setDragRect(null); setIsDragging(true); setSelectionPopup(null);
  }, [selectionMode]);

  const onPageTouchMove = useCallback((e) => {
    if (!isDragging || !dragStart) return;
    e.preventDefault();
    const t = e.touches[0];
    const pr = pageWrapRef.current.getBoundingClientRect();
    const cx = t.clientX - pr.left, cy = t.clientY - pr.top;
    setDragRect({ x: Math.min(dragStart.x, cx), y: Math.min(dragStart.y, cy), w: Math.abs(cx - dragStart.x), h: Math.abs(cy - dragStart.y) });
  }, [isDragging, dragStart]);

  const onPageTouchEnd = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);
    if (!dragRect || dragRect.w < 10 || dragRect.h < 10) { setDragRect(null); return; }
    cropCanvas(dragRect);
  }, [isDragging, dragRect, cropCanvas]);

  const handleAsk = useCallback(async () => {
    if (!selectionPopup || uploading) return;
    setUploading(true);
    try {
      if (selectionPopup.type === 'region') {
        const { image_url } = await uploadSelectionImage(set.id, selectionPopup.blob);
        const res = await pixelChat({ message: '', imageUrl: image_url, subject: 'general', language: language || 'en', studySetId: set.id });
        setSelectionPopup(null);
        setDragRect(null);
        setFullscreenChat({
          sessionId: res.session_id,
          initialMessages: [
            { role: 'user', content: '', imageUrl: image_url },
            { role: 'assistant', content: res.reply },
          ],
        });
      } else {
        const userMsg = `${tStudent.passageLabel || 'Selected passage'}:\n\n"${selectionPopup.text}"`;
        const res = await pixelChat({ message: userMsg, subject: 'general', language: language || 'en', studySetId: set.id });
        setSelectionPopup(null);
        setFullscreenChat({
          sessionId: res.session_id,
          initialMessages: [
            { role: 'user', content: userMsg },
            { role: 'assistant', content: res.reply },
          ],
        });
      }
    } catch { setSelectionPopup(null); }
    finally { setUploading(false); }
  }, [selectionPopup, uploading, set.id, language, tStudent.passageLabel]);

  const hasPdf = Boolean(pdfBlobUrl) && !pdfError;

  if (!set.pdf_url && !set.text_content) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem', color: '#aaa' }}>
        <FileText size={36} style={{ marginBottom: '0.75rem', opacity: 0.4 }} />
        <p style={{ margin: 0 }}>{tStudent.noTextContent || 'No text available from this PDF'}</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: '1rem', flexDirection: 'column' }}>
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>

        {/* PDF / Text pane */}
        <div style={{ flex: '1 1 320px', minWidth: 0 }} ref={pdfContainerRef}>

          {/* Mode toggle */}
          {hasPdf && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem', flexWrap: 'wrap', gap: '0.4rem' }}>
              <span style={{ fontSize: '0.78rem', color: '#888' }}>
                {selectionMode === 'text' ? (tStudent.selectTextPrompt || 'Select text to ask Pixel AI') : (tStudent.selectRegionPrompt || 'Drag to capture a formula or diagram')}
              </span>
              <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', border: '1px solid #e0d6f5' }}>
                {['text', 'region'].map(mode => (
                  <button key={mode} onClick={() => { setSelectionMode(mode); setDragRect(null); setSelectionPopup(null); setFloatBtn(null); }}
                    style={{ padding: '4px 12px', fontSize: '0.75rem', fontWeight: 600, border: 'none', cursor: 'pointer', background: selectionMode === mode ? '#5B35A0' : 'white', color: selectionMode === mode ? 'white' : '#5B35A0' }}>
                    {mode === 'text' ? 'Text' : 'Region'}
                  </button>
                ))}
              </div>
            </div>
          )}

          {pdfLoading ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#888', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} /> Loading PDF…
            </div>
          ) : hasPdf ? (
            <>
              {/* Page wrapper — drag events live here */}
              <div
                ref={pageWrapRef}
                style={{
                  position: 'relative', background: '#f4f4f4', borderRadius: 10,
                  border: '1px solid #e8edf5',
                  display: 'flex', justifyContent: 'center',
                  cursor: selectionMode === 'region' ? 'crosshair' : 'text',
                  userSelect: selectionMode === 'region' ? 'none' : 'text',
                }}
                onMouseDown={onPageMouseDown}
                onMouseMove={onPageMouseMove}
                onMouseUp={onPageMouseUp}
                onTouchStart={onPageTouchStart}
                onTouchMove={onPageTouchMove}
                onTouchEnd={onPageTouchEnd}
              >
                <Document
                  file={pdfBlobUrl}
                  onLoadSuccess={({ numPages: n }) => setNumPages(n)}
                  onLoadError={() => setPdfError(true)}
                  loading={<div style={{ padding: '3rem', color: '#888', display: 'flex', alignItems: 'center', gap: 8 }}><Loader size={18} style={{ animation: 'spin 1s linear infinite' }} /> Rendering…</div>}
                  error={<div style={{ padding: '2rem', color: '#e53935', fontSize: '0.85rem', textAlign: 'center' }}>Could not render PDF.</div>}
                >
                  <Page pageNumber={pageNumber} width={pdfWidth} renderTextLayer={selectionMode === 'text'} renderAnnotationLayer={false} />
                </Document>

                {/* Region drag rectangle */}
                {selectionMode === 'region' && dragRect && (
                  <div style={{ position: 'absolute', left: dragRect.x, top: dragRect.y, width: dragRect.w, height: dragRect.h, border: '2px solid #5B35A0', background: 'rgba(91,53,160,0.12)', pointerEvents: 'none' }} />
                )}
              </div>

              {/* Page controls */}
              {numPages && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.75rem', marginTop: '0.5rem' }}>
                  <button disabled={pageNumber <= 1} onClick={() => { setPageNumber(p => p - 1); setDragRect(null); setSelectionPopup(null); }}
                    style={{ background: 'none', border: '1px solid #ddd', borderRadius: 6, padding: '4px 10px', cursor: pageNumber <= 1 ? 'default' : 'pointer', opacity: pageNumber <= 1 ? 0.35 : 1, display: 'flex', alignItems: 'center' }}>
                    <ChevronLeft size={15} />
                  </button>
                  <span style={{ fontSize: '0.85rem', color: '#555', minWidth: 60, textAlign: 'center' }}>{pageNumber} / {numPages}</span>
                  <button disabled={pageNumber >= numPages} onClick={() => { setPageNumber(p => p + 1); setDragRect(null); setSelectionPopup(null); }}
                    style={{ background: 'none', border: '1px solid #ddd', borderRadius: 6, padding: '4px 10px', cursor: pageNumber >= numPages ? 'default' : 'pointer', opacity: pageNumber >= numPages ? 0.35 : 1, display: 'flex', alignItems: 'center' }}>
                    <ChevronRight size={15} />
                  </button>
                </div>
              )}

            </>
          ) : (
            <div style={{ background: '#fafafa', borderRadius: 10, padding: '1rem 1.25rem', border: '1px solid #e8edf5', fontSize: '0.87rem', lineHeight: 1.8, color: '#333', userSelect: 'text' }}>
              {(set.text_content || '').split(/\n{2,}/).filter(p => p.trim()).map((p, i) => (
                <p key={i} style={{ margin: '0 0 1rem' }}>{p}</p>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Floating "Ask about this" button — shows SelectionPopup */}
      {floatBtn && (
        <button
          onMouseDown={e => { e.preventDefault(); setSelectionPopup({ type: 'text', text: selectionText }); setFloatBtn(null); }}
          style={{
            position: 'fixed',
            left: floatBtn.x,
            top: floatBtn.y,
            transform: 'translateX(-50%)',
            zIndex: 9999,
            background: 'linear-gradient(135deg,#5B35A0,#667eea)',
            color: 'white', border: 'none', borderRadius: 20,
            padding: '6px 16px', fontSize: '0.78rem', fontWeight: 700,
            cursor: 'pointer', boxShadow: '0 4px 14px rgba(91,53,160,0.4)',
            whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 5,
          }}
        >
          <BookMarked size={13} /> {tStudent.askAboutSelection || 'Ask about this'}
        </button>
      )}

      {selectionPopup && (
        <SelectionPopup
          type={selectionPopup.type}
          dataUrl={selectionPopup.dataUrl}
          text={selectionPopup.text}
          uploading={uploading}
          onAsk={handleAsk}
          onClose={() => { setSelectionPopup(null); setDragRect(null); }}
          tStudent={tStudent}
        />
      )}

      {fullscreenChat && (
        <FullScreenChat
          sessionId={fullscreenChat.sessionId}
          initialMessages={fullscreenChat.initialMessages || []}
          studySetId={set.id}
          language={language}
          tStudent={tStudent}
          onClose={() => setFullscreenChat(null)}
        />
      )}
    </div>
  );
}

// ===========================
// STUDY SPACE TAB
// ===========================
function StudySpaceTab({ tStudent, navigate, initialStudySetId }) {
  const { language } = useLanguage();
  const [studySets, setStudySets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewModal, setShowNewModal] = useState(false);
  const [selectedSet, setSelectedSet] = useState(null);
  const [setDetail, setSetDetail] = useState(null);
  const [, setDetailLoading] = useState(false);
  const [pollingId, setPollingId] = useState(null);
  const [activeSetTab, setActiveSetTab] = useState('concepts');

  useEffect(() => {
    listStudySets()
      .then(sets => {
        setStudySets(sets);
        if (initialStudySetId) {
          const found = sets.find(s => s.id === initialStudySetId);
          if (found) openSet(initialStudySetId);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll processing sets
  useEffect(() => {
    const processing = studySets.find(s => s.status === 'processing');
    if (!processing) { if (pollingId) { clearInterval(pollingId); setPollingId(null); } return; }
    if (pollingId) return;
    const id = setInterval(() => {
      listStudySets().then(sets => {
        setStudySets(sets);
        if (!sets.find(s => s.status === 'processing')) { clearInterval(id); setPollingId(null); }
        if (selectedSet && sets.find(s => s.id === selectedSet)) {
          loadDetail(selectedSet);
        }
      }).catch(() => {});
    }, 4000);
    setPollingId(id);
    return () => clearInterval(id);
  }, [studySets, selectedSet, pollingId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadDetail = async (id) => {
    setDetailLoading(true);
    try {
      const d = await getStudySet(id);
      setSetDetail(d);
    } catch (e) { console.error(e); }
    finally { setDetailLoading(false); }
  };

  const openSet = async (id) => {
    setSelectedSet(id);
    await loadDetail(id);
  };

  const [truncatedWarning, setTruncatedWarning] = useState(false);

  const handleCreated = (set, wasTruncated) => {
    setStudySets(prev => [set, ...prev]);
    setShowNewModal(false);
    if (wasTruncated) setTruncatedWarning(true);
    openSet(set.id);
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this study set?')) return;
    try {
      await deleteStudySet(id);
      setStudySets(prev => prev.filter(s => s.id !== id));
      if (selectedSet === id) { setSelectedSet(null); setSetDetail(null); }
    } catch (er) { console.error(er); }
  };

  if (selectedSet && setDetail) {
    const set = setDetail;
    const isReady = set.status === 'ready';
    const setTabs = [
      { id: 'concepts', label: tStudent.conceptsTab || 'Concepts' },
      { id: 'read', label: tStudent.readTab || 'Read' },
    ];
    return (
      <div>
        <button onClick={() => { setSelectedSet(null); setSetDetail(null); setActiveSetTab('concepts'); }}
          style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', color: '#5B35A0', fontWeight: 600, fontSize: '0.87rem', marginBottom: '1rem', padding: 0 }}>
          <ChevronLeft size={16} /> {tStudent.backToSets || 'Back to Study Sets'}
        </button>

        {/* Set header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <div>
            <h2 style={{ margin: '0 0 0.3rem', fontSize: '1.1rem', color: '#1a1a2e' }}>{set.title}</h2>
            <span style={{
              fontSize: '0.73rem', padding: '2px 8px', borderRadius: 20, fontWeight: 600,
              background: isReady ? '#dcfce7' : '#fef3c7',
              color: isReady ? '#15803d' : '#92400e',
            }}>
              {isReady ? `${set.concepts?.length || 0} concepts` : (tStudent.processing || 'Processing...')}
            </span>
          </div>
          {!isReady && <Loader size={16} color="#f59e0b" style={{ animation: 'spin 1s linear infinite' }} />}
        </div>

        {/* Truncation warning */}
        {truncatedWarning && (
          <div style={{ background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 8, padding: '0.6rem 0.9rem', marginBottom: '0.75rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.82rem', color: '#92400e' }}>
            <span>{tStudent.pdfTruncated || 'PDF is too long — showing first 60 pages / 60,000 characters'}</span>
            <button onClick={() => setTruncatedWarning(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#92400e', padding: 0, marginLeft: 8 }}><X size={14} /></button>
          </div>
        )}

        {/* Sub-tabs */}
        <div style={{ display: 'flex', gap: '0.2rem', borderBottom: '1.5px solid #e8edf5', marginBottom: '1.25rem' }}>
          {setTabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveSetTab(tab.id)} style={{
              padding: '0.5rem 1rem', background: 'none', border: 'none', cursor: 'pointer',
              fontSize: '0.85rem', fontWeight: activeSetTab === tab.id ? 700 : 500,
              color: activeSetTab === tab.id ? '#5B35A0' : '#888',
              borderBottom: activeSetTab === tab.id ? '2.5px solid #5B35A0' : '2.5px solid transparent',
              marginBottom: -1.5, borderRadius: 0,
            }}>{tab.label}</button>
          ))}
        </div>

        {/* Concepts tab */}
        {activeSetTab === 'concepts' && (
          isReady && set.concepts?.length > 0 ? (
            <div className="studio-card-grid">
              {set.concepts.map((concept, i) => (
                <div key={concept.id} className="studio-card" style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/studio/student/concepts/${concept.id}?study=true`, {
                    state: { conceptTitle: concept.title, conceptSummary: concept.summary, subject: 'general', studySetId: set.id }
                  })}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                    <div style={{ width: 30, height: 30, borderRadius: '50%', background: '#f0ebff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '0.78rem', fontWeight: 700, color: '#5B35A0' }}>{i + 1}</div>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ margin: '0 0 0.3rem', fontSize: '0.92rem', fontWeight: 700, color: '#1a1a2e' }}>{concept.title}</h3>
                      {concept.summary && <p style={{ margin: 0, color: '#666', fontSize: '0.8rem', lineHeight: 1.4 }}>{concept.summary.slice(0, 100)}{concept.summary.length > 100 ? '…' : ''}</p>}
                    </div>
                  </div>
                  <div style={{ marginTop: '0.75rem', display: 'flex', justifyContent: 'flex-end' }}>
                    <button className="btn-primary" style={{ padding: '0.28rem 0.75rem', fontSize: '0.78rem' }}>
                      {tStudent.studyNow || 'Study Now'} <ChevronRight size={12} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : isReady ? (
            <div className="empty-state"><AlertCircle size={32} color="#f59e0b" /><p style={{ color: '#888', marginTop: '0.5rem' }}>No concepts extracted.</p></div>
          ) : (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
              <Loader size={26} color="#5B35A0" style={{ animation: 'spin 1s linear infinite', marginBottom: '0.75rem' }} />
              <p style={{ margin: 0 }}>{tStudent.extractingConcepts || 'Extracting concepts...'}</p>
            </div>
          )
        )}

        {/* Read tab */}
        {activeSetTab === 'read' && (
          <PdfReader set={set} tStudent={tStudent} language={language} />
        )}
      </div>
    );
  }

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  return (
    <>
      <div className="section-header">
        <h2><Layers size={18} />{tStudent.studySpace || 'Study Space'}</h2>
        <button className="btn-primary" onClick={() => setShowNewModal(true)}>
          <Plus size={16} /> {tStudent.newStudySet || 'New Study Set'}
        </button>
      </div>

      {studySets.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon"><FileText size={48} /></div>
          <h3>{tStudent.noStudySets || 'No study materials yet'}</h3>
          <p>{tStudent.noStudySetsDesc || 'Upload a PDF to get started'}</p>
          <button className="btn-primary" onClick={() => setShowNewModal(true)}>
            <Upload size={16} /> {tStudent.uploadPdf || 'Upload PDF'}
          </button>
        </div>
      ) : (
        <div className="studio-card-grid">
          {studySets.map(s => {
            const isReady = s.status === 'ready';
            return (
              <div key={s.id} className="studio-card" style={{ cursor: 'pointer' }} onClick={() => openSet(s.id)}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                    <FileText size={18} color="#5B35A0" />
                    <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700, color: '#1a1a2e' }}>{s.title}</h3>
                  </div>
                  <button onClick={(e) => handleDelete(s.id, e)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#bbb', padding: 2 }}>
                    <Trash2 size={15} />
                  </button>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.75rem', fontSize: '0.8rem', color: '#888' }}>
                  <span style={{
                    padding: '2px 8px', borderRadius: 20, fontWeight: 600, fontSize: '0.73rem',
                    background: isReady ? '#dcfce7' : '#fef3c7',
                    color: isReady ? '#15803d' : '#92400e',
                  }}>
                    {isReady ? `${s.concept_count || 0} concepts` : (tStudent.processing || 'Processing...')}
                  </span>
                  {isReady && (
                    <button className="btn-secondary" style={{ padding: '0.25rem 0.6rem', fontSize: '0.78rem' }}>
                      {tStudent.studyNow || 'Study Now'} <ChevronRight size={12} />
                    </button>
                  )}
                  {!isReady && <Loader size={14} color="#f59e0b" style={{ animation: 'spin 1s linear infinite' }} />}
                </div>
              </div>
            );
          })}
          <div className="studio-card" style={{
            cursor: 'pointer', border: '2px dashed #c5d0e8',
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', minHeight: 120, gap: '0.75rem', background: '#f8f9ff',
          }} onClick={() => setShowNewModal(true)}>
            <Plus size={24} color="#5B35A0" />
            <span style={{ color: '#5B35A0', fontWeight: 600, fontSize: '0.87rem' }}>
              {tStudent.newStudySet || 'New Study Set'}
            </span>
          </div>
        </div>
      )}

      {showNewModal && (
        <NewStudySetModal
          tStudent={tStudent}
          onCreated={handleCreated}
          onClose={() => setShowNewModal(false)}
        />
      )}
    </>
  );
}

// ===========================
// HISTORY TAB
// ===========================
const SOURCE_STYLES = {
  course:    { bg: '#e8f4ff', color: '#2563eb', icon: BookOpen,    label: 'Course' },
  study_set: { bg: '#f0ebff', color: '#5B35A0', icon: FileText,    label: 'Study Set' },
  reading:   { bg: '#fef3c7', color: '#92400e', icon: BookMarked,  label: 'Reading' },
};

function HistoryTab({ tStudent }) {
  const { language } = useLanguage();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openTranscript, setOpenTranscript] = useState(null);
  const [fullscreenChat, setFullscreenChat] = useState(null);

  useEffect(() => {
    getStudentHistory()
      .then(data => setSessions(data.sessions || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  };

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  if (sessions.length === 0) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon"><History size={48} /></div>
        <h3>{tStudent.noHistory || 'No sessions yet'}</h3>
        <p>{tStudent.noHistoryDesc || 'Start chatting with Pixel AI to see your history here'}</p>
      </div>
    );
  }

  return (
    <>
      <div className="section-header">
        <h2><History size={18} />{tStudent.history || 'History'}</h2>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
        {sessions.map(s => {
          const style = SOURCE_STYLES[s.source] || SOURCE_STYLES.course;
          const Icon = style.icon;
          const title = s.concept_title || s.session_title || 'Session';
          const subtitle = s.source === 'reading' && s.passage_snippet
            ? `"${s.passage_snippet.slice(0, 80)}…"`
            : s.course_title || '';
          const refImage = s.source === 'reading' ? s.passage_image_url : null;
          return (
            <div key={s.id} className="studio-card"
              style={{ padding: '0.8rem 1rem', cursor: 'pointer' }}
              onClick={() => setOpenTranscript(s)}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                <div style={{ width: 34, height: 34, borderRadius: '50%', flexShrink: 0, background: style.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon size={15} color={style.color} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.88rem', color: '#1a1a2e', marginBottom: '0.15rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {title}
                  </div>
                  {subtitle && (
                    <div style={{ fontSize: '0.77rem', color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: '0.2rem' }}>
                      {subtitle}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '0.65rem', fontSize: '0.75rem', color: '#aaa', flexWrap: 'wrap' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                      <Clock size={10} /> {formatDate(s.updated_at || s.created_at)}
                    </span>
                    <span style={{ padding: '1px 7px', borderRadius: 20, fontWeight: 600, fontSize: '0.7rem', background: style.bg, color: style.color }}>
                      {s.source === 'reading' ? (tStudent.readingSession || 'Reading') : s.source === 'study_set' ? (tStudent.studySetLabel || 'Study Set') : (tStudent.course || 'Course')}
                    </span>
                    {s.message_count > 0 && (
                      <span>{s.message_count} {tStudent.msgCount || 'messages'}</span>
                    )}
                  </div>
                </div>
                {refImage && (
                  <img src={refImage} alt="" style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6, border: '1px solid #e8edf5', flexShrink: 0 }} />
                )}
                <ChevronRight size={15} color="#bbb" style={{ flexShrink: 0, marginTop: 4 }} />
              </div>
            </div>
          );
        })}
      </div>

      {openTranscript && (
        <TranscriptModal
          sessionId={openTranscript.id}
          title={openTranscript.concept_title || openTranscript.session_title || 'Session'}
          passageImageUrl={openTranscript.passage_image_url || null}
          tStudent={tStudent}
          onClose={() => setOpenTranscript(null)}
          onContinueChat={() => {
            setOpenTranscript(null);
            setFullscreenChat({ sessionId: openTranscript.id, initialMessages: [] });
          }}
        />
      )}

      {fullscreenChat && (
        <FullScreenChat
          sessionId={fullscreenChat.sessionId}
          initialMessages={fullscreenChat.initialMessages}
          language={language}
          tStudent={tStudent}
          onClose={() => setFullscreenChat(null)}
        />
      )}
    </>
  );
}

// ===========================
// MAIN DASHBOARD
// ===========================
export default function StudentDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { language } = useLanguage();
  const t = (translations[language]?.studio) || {};
  const tStudent = t.student || {};

  const [activeTab, setActiveTab] = useState(location.state?.tab || 'courses');
  const initialStudySetId = location.state?.studySetId || null;
  const [courses, setCourses] = useState([]);
  const [courseNotifs, setCourseNotifs] = useState({});
  const [loading, setLoading] = useState(true);
  const [showEnroll, setShowEnroll] = useState(false);
  const [showRightMenu, setShowRightMenu] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [knowledgeLevel, setKnowledgeLevel] = useState('intermediate');

  const handleOpenSubscription = () => { setShowSubscriptionModal(true); document.body.classList.add('modal-open'); };
  const handleCloseSubscription = () => { setShowSubscriptionModal(false); document.body.classList.remove('modal-open'); };

  useEffect(() => {
    getMyCourses()
      .then(setCourses)
      .catch(console.error)
      .finally(() => setLoading(false));
    getMyNotifications()
      .then(data => {
        const map = {};
        (data.courses || []).forEach(n => { map[n.course_id] = n; });
        setCourseNotifs(map);
      })
      .catch(console.error);
    getStudentProfile()
      .then(p => { if (p?.knowledge_level) setKnowledgeLevel(p.knowledge_level); })
      .catch(console.error);
  }, []);

  const handleLevelChange = async (level) => {
    setKnowledgeLevel(level);
    await updateStudentProfile(level);
  };

  const handleEnrolled = (courseId) => {
    setShowEnroll(false);
    navigate(`/studio/student/courses/${courseId}`);
  };

  const user = authService.getUser();
  const hasCourses = loading || courses.length > 0;
  const allTabs = [
    { id: 'courses', label: tStudent.myCourses || 'My Courses', icon: BookOpen },
    { id: 'study', label: tStudent.studySpace || 'Study Space', icon: Layers },
    { id: 'history', label: tStudent.history || 'History', icon: History },
  ];
  const tabs = hasCourses ? allTabs : allTabs.filter(t => t.id !== 'courses');

  // If courses tab was active but student has no courses, redirect to study
  useEffect(() => {
    if (!loading && courses.length === 0 && activeTab === 'courses') {
      setActiveTab('study');
    }
  }, [loading, courses.length, activeTab]);

  return (
    <div className="studio-page">
      <UnifiedHeader onOpenMenu={() => setShowRightMenu(true)} />

      <div className="studio-main">
        {/* Welcome banner */}
        <div className="studio-card" style={{
          background: 'linear-gradient(135deg, #5B35A0 0%, #2D1B5E 100%)',
          color: 'white', marginBottom: '1.5rem',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem',
        }}>
          <div>
            <h2 style={{ margin: '0 0 0.25rem', fontSize: '1.3rem' }}>
              {tStudent.welcome || 'Welcome'}{user?.fullName ? `, ${user.fullName.split(' ')[0]}` : ''}!
            </h2>
            <p style={{ margin: 0, opacity: 0.85, fontSize: '0.9rem' }}>
              {tStudent.welcomeDesc || 'Keep learning and track your progress below'}
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            <KnowledgeLevelBadge level={knowledgeLevel} tStudent={tStudent} onChange={handleLevelChange} />
            <button className="btn-primary"
              style={{ background: 'rgba(255,255,255,0.2)', border: '1.5px solid rgba(255,255,255,0.5)', color: 'white' }}
              onClick={() => setShowEnroll(true)}>
              <Plus size={16} /> {tStudent.joinCourse || 'Join Course'}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '1.5rem', borderBottom: '1.5px solid #e8edf5', paddingBottom: 0 }}>
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '0.6rem 1rem', background: 'none', border: 'none', cursor: 'pointer',
                fontSize: '0.875rem', fontWeight: activeTab === tab.id ? 700 : 500,
                color: activeTab === tab.id ? '#5B35A0' : '#888',
                borderBottom: activeTab === tab.id ? '2.5px solid #5B35A0' : '2.5px solid transparent',
                marginBottom: -1, borderRadius: 0,
              }}>
                <Icon size={15} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {activeTab === 'courses' && (
          <CoursesTab
            courses={courses}
            courseNotifs={courseNotifs}
            loading={loading}
            tStudent={tStudent}
            onEnroll={() => setShowEnroll(true)}
            navigate={navigate}
          />
        )}
        {activeTab === 'study' && <StudySpaceTab tStudent={tStudent} navigate={navigate} initialStudySetId={initialStudySetId} />}
        {activeTab === 'history' && <HistoryTab tStudent={tStudent} />}
      </div>

      {showEnroll && <EnrollModal onEnroll={handleEnrolled} onClose={() => setShowEnroll(false)} tStudent={tStudent} />}

      <RightSideMenu
        isOpen={showRightMenu}
        onClose={() => setShowRightMenu(false)}
        user={user}
        currentPlan="free"
        onOpenSubscription={handleOpenSubscription}
      />
      <SubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={handleCloseSubscription}
        currentPlan="free"
      />
    </div>
  );
}
